import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Progress } from '@/shared/components/ui/progress';
import { toast } from 'sonner';
import {
  Share2,
  Plus,
  CheckCircle,
  AlertCircle,
  RefreshCcw,
  Settings,
  Trash2,
  ExternalLink,
  Instagram,
  Facebook,
  Linkedin,
  Youtube,
  Twitter,
  Mail,
  MessageCircle,
  BarChart3,
  Users,
  Eye,
  MousePointer,
  Heart } from
'lucide-react';

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const providerConfig = {
  instagram: {
    name: 'Instagram',
    icon: Instagram,
    color: 'from-pink-500 to-purple-600',
    bgColor: 'bg-gradient-to-br from-pink-100 to-purple-100',
    textColor: 'text-pink-600'
  },
  facebook: {
    name: 'Facebook',
    icon: Facebook,
    color: 'from-blue-500 to-blue-700',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-600'
  },
  linkedin: {
    name: 'LinkedIn',
    icon: Linkedin,
    color: 'from-sky-500 to-blue-600',
    bgColor: 'bg-sky-100',
    textColor: 'text-sky-600'
  },
  youtube: {
    name: 'YouTube',
    icon: Youtube,
    color: 'from-red-500 to-red-700',
    bgColor: 'bg-red-100',
    textColor: 'text-red-600'
  },
  x: {
    name: 'X (Twitter)',
    icon: Twitter,
    color: 'from-gray-700 to-gray-900',
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-700'
  },
  tiktok: {
    name: 'TikTok',
    icon: MessageCircle,
    color: 'from-gray-900 to-pink-500',
    bgColor: 'bg-gradient-to-br from-gray-100 to-pink-100',
    textColor: 'text-gray-800'
  },
  threads: {
    name: 'Threads',
    icon: MessageCircle,
    color: 'from-gray-800 to-gray-900',
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-800'
  },
  email_gmail: {
    name: 'Gmail',
    icon: Mail,
    color: 'from-red-500 to-amber-500',
    bgColor: 'bg-red-50',
    textColor: 'text-red-600'
  },
  email_outlook: {
    name: 'Outlook',
    icon: Mail,
    color: 'from-blue-500 to-sky-500',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-600'
  },
  email_mailchimp: {
    name: 'Mailchimp',
    icon: Mail,
    color: 'from-yellow-400 to-amber-500',
    bgColor: 'bg-yellow-50',
    textColor: 'text-yellow-700'
  },
  google_ads: {
    name: 'Google Ads',
    icon: BarChart3,
    color: 'from-blue-500 via-green-500 to-yellow-500',
    bgColor: 'bg-gradient-to-br from-blue-50 via-green-50 to-yellow-50',
    textColor: 'text-blue-600'
  },
  meta_ads: {
    name: 'Meta Ads',
    icon: BarChart3,
    color: 'from-blue-500 to-indigo-600',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-600'
  }
};

// Mock connected accounts for demo
const mockConnectedAccounts = [
{ id: 1, provider: 'instagram', name: '@mybrand', username: 'mybrand', status: 'active', followers: 12500, posts: 234, engagement: 4.2 },
{ id: 2, provider: 'facebook', name: 'My Brand Page', username: 'mybrand', status: 'active', followers: 8900, posts: 156, engagement: 2.8 },
{ id: 3, provider: 'linkedin', name: 'My Brand Company', username: 'mybrand-co', status: 'active', followers: 3200, posts: 89, engagement: 5.1 }];


export const Channels = () => {const { t } = useTranslation("cns");
  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState([]);
  const [connectedAccounts, setConnectedAccounts] = useState(mockConnectedAccounts);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);

  useEffect(() => {
    fetchProviders();
  }, []);

  const fetchProviders = async () => {
    try {
      const res = await fetch(`${API_URL}/api/cns/accounts/providers`);
      if (res.ok) {
        const data = await res.json();
        setProviders(data.providers || []);
      }
    } catch (err) {
      console.error('Failed to fetch providers');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = (provider) => {
    // In real implementation, this would initiate OAuth flow
    toast.info(`OAuth flow for ${provider.name} would start here. API keys can be configured later.`);
    setShowConnectModal(false);
  };

  const handleDisconnect = (accountId) => {
    setConnectedAccounts(connectedAccounts.filter((a) => a.id !== accountId));
    toast.success('Account disconnected');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="channels">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3 font-['Space_Grotesk']">
            <div className="p-2 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl">
              <Share2 className="h-7 w-7 text-white" />
            </div>{t("connectedChannels", "Connected Channels")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("manageYourSocial", "Manage your social media and marketing channel connections")}

          </p>
        </div>
        <Button
          className="gap-2 bg-gradient-to-r from-teal-500 to-emerald-600"
          onClick={() => setShowConnectModal(true)}>
          
          <Plus className="h-4 w-4" />{t("connectChannel", "Connect Channel")}

        </Button>
      </div>

      {/* Connected Accounts */}
      {connectedAccounts.length > 0 &&
      <div className="space-y-4">
          <h2 className="text-lg font-semibold">{t("connectedAccounts", "Connected Accounts")}</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {connectedAccounts.map((account) => {
            const config = providerConfig[account.provider] || {};
            const Icon = config.icon || Share2;
            return (
              <Card key={account.id} className="card-elevated">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-3 rounded-xl ${config.bgColor}`}>
                          <Icon className={`h-6 w-6 ${config.textColor}`} />
                        </div>
                        <div>
                          <CardTitle className="text-base">{account.name}</CardTitle>
                          <CardDescription>@{account.username}</CardDescription>
                        </div>
                      </div>
                      <Badge
                      className={account.status === 'active' ?
                      'bg-emerald-100 text-emerald-700 border-emerald-200' :
                      'bg-red-100 text-red-700 border-red-200'
                      }>
                      
                        {account.status === 'active' ?
                      <><CheckCircle className="h-3 w-3 mr-1" />{t("active", "Active")}</> :

                      <><AlertCircle className="h-3 w-3 mr-1" />{t("error", "Error")}</>
                      }
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div className="text-center">
                        <p className="text-lg font-bold">{(account.followers / 1000).toFixed(1)}K</p>
                        <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                          <Users className="h-3 w-3" />{t("followers", "Followers")}
                      </p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold">{account.posts}</p>
                        <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                          <Eye className="h-3 w-3" />{t("posts", "Posts")}
                      </p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold">{account.engagement}%</p>
                        <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                          <Heart className="h-3 w-3" />{t("eng", "Eng.")}
                      </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" className="flex-1 gap-1">
                        <RefreshCcw className="h-3 w-3" />{t("refresh", "Refresh")}
                    </Button>
                      <Button variant="outline" size="sm" className="gap-1">
                        <Settings className="h-3 w-3" />
                      </Button>
                      <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDisconnect(account.id)}>
                      
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>);

          })}
          </div>
        </div>
      }

      {/* Available Providers */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold">{t("availableChannels", "Available Channels")}</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {providers.map((provider) => {
            const config = providerConfig[provider.id] || {};
            const Icon = config.icon || Share2;
            const isConnected = connectedAccounts.some((a) => a.provider === provider.id);

            return (
              <Card
                key={provider.id}
                className={`card-elevated cursor-pointer transition-all hover:shadow-lg ${isConnected ? 'opacity-60' : ''}`}
                onClick={() => !isConnected && handleConnect(provider)}>
                
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`p-2 rounded-lg ${config.bgColor || 'bg-gray-100'}`}>
                      <Icon className={`h-5 w-5 ${config.textColor || 'text-gray-600'}`} />
                    </div>
                    <div>
                      <p className="font-medium">{provider.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {provider.account_types?.join(', ')}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {provider.supports_publish &&
                    <Badge variant="outline" className="text-xs">{t("publish", "Publish")}</Badge>
                    }
                    {provider.supports_schedule &&
                    <Badge variant="outline" className="text-xs">{t("schedule", "Schedule")}</Badge>
                    }
                    {provider.supports_analytics &&
                    <Badge variant="outline" className="text-xs">{t("analytics", "Analytics")}</Badge>
                    }
                    {provider.supports_ads &&
                    <Badge variant="outline" className="text-xs">{t("ads", "Ads")}</Badge>
                    }
                  </div>
                  {provider.needs_user_action &&
                  <p className="text-xs text-amber-600 mb-2">
                      <AlertCircle className="h-3 w-3 inline mr-1" />
                      {provider.needs_user_action_reason || 'Manual action may be required'}
                    </p>
                  }
                  <Button
                    className={`w-full gap-2 ${isConnected ? 'bg-gray-200 text-gray-500' : `bg-gradient-to-r ${config.color || 'from-gray-500 to-gray-700'} text-white`}`}
                    disabled={isConnected}
                    size="sm">
                    
                    {isConnected ?
                    <><CheckCircle className="h-4 w-4" />{t("connected", "Connected")}</> :

                    <><Plus className="h-4 w-4" />{t("connect", "Connect")}</>
                    }
                  </Button>
                </CardContent>
              </Card>);

          })}
        </div>
      </div>

      {/* Connect Modal */}
      {showConnectModal &&
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg mx-4">
            <CardHeader>
              <CardTitle>{t("connectAChannel", "Connect a Channel")}</CardTitle>
              <CardDescription>{t("selectAPlatform", "Select a platform to connect your account")}

            </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                {providers.slice(0, 8).map((provider) => {
                const config = providerConfig[provider.id] || {};
                const Icon = config.icon || Share2;
                return (
                  <Button
                    key={provider.id}
                    variant="outline"
                    className="h-auto py-4 flex flex-col items-center gap-2"
                    onClick={() => handleConnect(provider)}>
                    
                      <div className={`p-2 rounded-lg ${config.bgColor || 'bg-gray-100'}`}>
                        <Icon className={`h-5 w-5 ${config.textColor || 'text-gray-600'}`} />
                      </div>
                      <span className="text-sm">{provider.name}</span>
                    </Button>);

              })}
              </div>
              <div className="flex justify-end mt-4">
                <Button variant="outline" onClick={() => setShowConnectModal(false)}>{t("cancel", "Cancel")}

              </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      }
    </div>);

};

export default Channels;